import React, { useEffect, useState } from 'react';
import {
  Divider,
  Tab,
  Tabs,
  Typography,
  makeStyles,
  Box,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { PageToolbar } from 'components/PageToolbar';
import { DepartmentsTab } from 'features/organizations/organizationTabsContent/DepartmentsTab';
import { OrganizationDetailsTab } from 'features/organizations/organizationTabsContent/OrganizationDetailsTab';
import { ContactsTab } from 'features/organizations/organizationTabsContent/ContactsTab';
import { ContractTab } from 'features/organizations/organizationTabsContent/ContractTab';
import { MeasurementCardTab } from 'features/organizations/organizationTabsContent/MeasurementCardTab';
import { UsersTab } from 'features/organizations/organizationTabsContent/UsersTab';
import { useSelector } from 'react-redux';
import { history } from 'helpers/history';
import 'theme/iconClasses.css';
import { ApplicationState } from 'store';
import { SawmillsTab } from 'features/organizations/organizationTabsContent/SawmillsTab';

const useStyles = makeStyles(() => ({
  alignRight: {
    textAlign: 'right',
  },
  buttonClass: {
    marginLeft: 0,
  },
  toolbarTitle: {
    minHeight: '1.8rem',
  },
  plusIcon: {
    display: 'inline-block',
    fontSize: '1.5rem',
    lineHeight: 0,
    marginRight: '0.5rem',
  },

  flexContainer: {
    justifyContent: 'space-between',
  },

  root: {
    marginBottom: '-1rem',
  },
  tabPanel: {
    flex: 1,
  },
  tabBox: {
    height: '100%',
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  const classes = useStyles();
  return (
    <div
      className={classes.tabPanel}
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box className={classes.tabBox} p={3}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const Organization = () => {
  const classes = useStyles();
  const { t } = useTranslation(['translations']);
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const tabIdParam = urlParams.get('tab');
  const tabId = tabIdParam && +tabIdParam;
  const [tab, setTab] = useState(tabId || 0);

  const organizationName: string | undefined = useSelector(
    (state: ApplicationState) => state.organization?.organization?.name,
  );

  // eslint-disable-next-line @typescript-eslint/ban-types
  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTab(newValue);
    history.push(`${location.pathname}?tab=${newValue}`);
  };

  useEffect(() => {
    setTab(tabId || 0);
  }, [tabId]);

  return (
    <>
      <PageToolbar>
        <Typography variant="subtitle1" className={classes.toolbarTitle}>
          {organizationName}
        </Typography>
        <Divider />
        <Tabs
          value={tab}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          classes={{
            // flexContainer: classes.flexContainer,
            root: classes.root,
          }}
        >
          <Tab
            label={t('organization_page.tabs.organization_information')}
            icon={<div className="organization-details-icon"></div>}
            {...a11yProps(0)}
          />
          <Tab
            label={t('organization_page.tabs.contract')}
            icon={<div className="contract-icon"></div>}
            {...a11yProps(1)}
          />
          <Tab
            label={t('organization_page.tabs.contact_person')}
            icon={<div className="contacts-icon"></div>}
            {...a11yProps(2)}
          />
          <Tab
            label={t('organization_page.tabs.measurement_card')}
            icon={<div className="measurement-card-icon"></div>}
            {...a11yProps(3)}
          />
          <Tab
            label={t('organization_page.tabs.departments')}
            icon={<div className="departments-icon"></div>}
            {...a11yProps(4)}
          />
          <Tab
            label={t('organization_page.tabs.users')}
            icon={<div className="users-icon"></div>}
            {...a11yProps(5)}
          />
          <Tab
            label={t('organization_page.tabs.sawmills')}
            icon={<div className="sawmill-icon"></div>}
            {...a11yProps(6)}
          />
        </Tabs>
      </PageToolbar>
      <TabPanel value={tab} index={0}>
        <OrganizationDetailsTab />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <ContractTab />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <ContactsTab />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <MeasurementCardTab />
      </TabPanel>
      <TabPanel value={tab} index={4}>
        <DepartmentsTab />
      </TabPanel>
      <TabPanel value={tab} index={5}>
        <UsersTab />
      </TabPanel>
      <TabPanel value={tab} index={6}>
        <SawmillsTab />
      </TabPanel>
    </>
  );
};

export { Organization };
